import sources from 'helpers/sources';
import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../components/button';
import Checkbox from '../../components/checkbox';
import RadioButtons from '../../components/radio_buttons';
import { Gym } from '../../types/gym';
import notification from 'helpers/notification';
import toFixed from 'helpers/to_fixed';
import api from 'helpers/api';
import currencyFormatter from 'helpers/currency_formatter';
import dateFormatter from 'helpers/date_formatter';
import { useAngularService } from 'helpers/get_angular_service';

const actionOptions = [
  {
    value: 'refund',
    label:
      'Return amount(s) back to the voucher(s), to be re-used later. If the voucher has been removed, a replacement will be created automatically.',
  },
  {
    value: 'remove',
    label:
      'Remove credit from payment, do not return it back to the voucher. The credit amount will be permanently lost.',
  },
];

const RemoveCreditModal = ({ modalInstance, invoice }) => {
  const $rootScope = useAngularService('$rootScope');
  const gym: Gym = $rootScope.gym;
  const [credits, setCredits] = useState([]);
  const [selectedCredits, setSelectedCredits] = useState([]);
  const [action, setAction] = useState();
  const [isLoading, setLoading] = useState(false);

  const totalToRemove = useMemo(() => {
    const total = credits.reduce((acc, item) => {
      return selectedCredits.indexOf(item.id) >= 0 ? acc + +item.amount : acc;
    }, 0);
    return toFixed(Math.abs(total));
  }, [selectedCredits]);

  const cancel = () => {
    modalInstance.dismiss('cancel');
  };

  useEffect(() => {
    const url = sources.creditVoucherActivities(gym.id);
    api
      .get(url, {
        filters: {
          invoice_number: invoice.invoice_number,
          removed: false,
          category: 'credit_applied',
        },
      })
      .then((response) => {
        setCredits(response);
        const ids = response.map((item) => item.id);
        setSelectedCredits(ids);
      });
  }, []);

  const submit = () => {
    setLoading(true);
    const actionUrl =
      action === 'remove' ? 'remove_credit_from_target' : 'refund_credit_from_target';
    const url = `${sources.creditVoucherActivities(gym.id)}/${actionUrl}`;

    const params = {
      redemption_target_type: 'Invoice',
      redemption_target_id: invoice.id,

      activity_ids_to_remove:
        credits.length === selectedCredits.length ? undefined : selectedCredits,
    };
    api.post(url, { credit_voucher_activity: params }).then(
      () => {
        $rootScope.$emit('invoices:list:reload');
        $rootScope.$emit('contact:reload');
        const message =
          action === 'remove' ? 'Credit removed successfully' : 'Credit refunded successfully';
        notification.success(message);
        cancel();
      },
      () => {
        notification.error('Request could not be completed. Please try again');
        setLoading(false);
      },
    );
  };

  const isReady = () => {
    return action && selectedCredits.length > 0 && !isLoading;
  };

  const isSelected = (id) => {
    return selectedCredits.indexOf(id) >= 0;
  };

  const toggleCredit = (checked, id) => {
    if (checked) {
      setSelectedCredits((items) => {
        return items.concat([id]);
      });
    } else {
      setSelectedCredits((items) => {
        return items.filter((item) => item !== id);
      });
    }
  };

  return (
    <div>
      <div className="modal-header">
        <a aria-hidden="true" className="close-modal" onClick={cancel} title="Close">
          <i className="icon-cross"></i>
        </a>
        <h4 className="modal-title">Remove credit from Invoice #{invoice.invoice_number_base}</h4>
      </div>
      <div className="modal-body modal-body-visible">
        <div className="margin-bottom-16">
          Invoice: {invoice.invoice_number_base}: {invoice.membership_label}
          <br />
          {currencyFormatter(invoice.amount, gym.currency_symbol)}, due{' '}
          {dateFormatter(invoice.due_date, 'DD-MMM-YYYY')}
        </div>

        <div className="margin-bottom-16">
          Total credit applied:{' '}
          <strong>{currencyFormatter(invoice.credit_applied_amount, gym.currency_symbol)}</strong>
        </div>

        <div className="margin-bottom-16">
          <strong>Please select the credit amount(s) to remove:</strong>
        </div>

        {credits.map((credit) => (
          <div className="margin-bottom-8 margin-left" key={credit.id}>
            <Checkbox
              checked={isSelected(credit.id)}
              onChange={(checked) => {
                toggleCredit(checked, credit.id);
              }}
            >
              {currencyFormatter(-credit.amount, gym.currency_symbol)}, applied on date by{' '}
              {credit.redeemed_by_name}
              <br />
              {credit.credit_voucher_category_name}{' '}
              {credit.voucher_reference || credit.credit_voucher_description}
            </Checkbox>
          </div>
        ))}

        <div className="margin-bottom-16">
          Total credit to remove:{' '}
          <strong>{currencyFormatter(totalToRemove, gym.currency_symbol)}</strong>
        </div>

        <div className="margin-bottom-8">
          <strong>What should happen to this credit?</strong>
        </div>

        <RadioButtons
          options={actionOptions}
          value={action}
          getValue={(item) => item.value}
          getLabel={(item) => item.label}
          onChange={(val) => setAction(val)}
          vertical
        />
      </div>
      <div className="modal-footer">
        <Button variant="primary" onClick={() => submit()} disabled={!isReady()}>
          Remove credit
        </Button>

        <Button onClick={cancel}>Cancel</Button>
      </div>
    </div>
  );
};

angular.module('gym').value('RemoveCreditModal', RemoveCreditModal);
